/**
 * Типизированный клиент нашего REST API (STEP 11).
 * Авторизация — заголовком `Authorization: tma <initData>`, ровно как ждёт сервер.
 */
import { telegramEnv } from './telegram.js';
import type { ApiTrade, Candle, TerminalAsset } from './terminalLogic.js';

const BASE_URL = (import.meta.env['VITE_API_URL'] ?? '').replace(/\/+$/, '');

/**
 * Бот, который открыл прилу (мульти-бот): `?b=<id>` ставит `webAppUrlForBot`.
 * Публичные роуты (конфиг, тексты) по нему отдают бренд, языки и тему нужного бота;
 * у основного бота параметра нет — всё как раньше.
 */
const BOT_PARAM = (() => {
  try {
    const value = new URLSearchParams(globalThis.location?.search ?? '').get('b');
    return value && /^\d{1,9}$/.test(value) ? value : null;
  } catch {
    return null;
  }
})();

/** Добавляет к адресу `b=<id>`, сохраняя уже имеющиеся параметры. */
function withBot(path: string): string {
  if (!BOT_PARAM) return path;
  return `${path}${path.includes('?') ? '&' : '?'}b=${BOT_PARAM}`;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: unknown,
    message: string,
    fromServer = false,
  ) {
    super(message);
    this.name = 'ApiError';
    this.fromServer = fromServer;
  }

  /** 402 — не хватает токенов на цикл. */
  get isOutOfTokens(): boolean {
    return this.status === 402;
  }

  /** 409 — аккаунт брокера занят другим пользователем. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * true — текст ошибки прислал наш сервер и его можно показать как есть.
   * false — сообщение техническое (например, 502 от nginx), экран должен
   * подставить свою локализованную формулировку.
   */
  readonly fromServer: boolean = false;
}

/**
 * Текст ошибки, который не стыдно показать пользователю.
 * Возвращает null, когда сервер не прислал внятного сообщения (502 от nginx,
 * обрыв сети) — экран в этом случае подставляет свою локализованную фразу.
 */
export function serverError(err: unknown): string | null {
  if (err instanceof ApiError) return err.fromServer ? err.message : null;
  return null;
}

/**
 * Коды, которыми сервер отвечает вместо готовой фразы.
 *
 * Текст к ним лежит в словаре — иначе пользователь увидел бы служебное
 * `account_required`, да ещё и на языке сервера, а не на своём.
 */
const ERROR_KEYS: Record<string, string> = {
  account_required: 'app.error.account_required',
  broker_unavailable: 'app.error.broker_unavailable',
  // переключение на REAL: наш аккаунт + хотя бы один депозит
  deposit_required: 'app.error.real_deposit_required',
  foreign_account: 'app.error.real_foreign',
  // refresh-токен брокера умер и тихий перелогин не удался — нужен вход заново
  broker_session_expired: 'app.error.broker_session_expired',
};

/**
 * Готовый текст ошибки для экрана: сначала известный код, затем внятное
 * сообщение сервера, затем запасной ключ.
 */
export function apiMessage(
  err: unknown,
  t: (key: string) => string,
  fallbackKey: string,
): string {
  if (err instanceof ApiError) {
    const payload = err.payload;
    const code =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null;
    if (code && ERROR_KEYS[code]) return t(ERROR_KEYS[code]);
  }
  return serverError(err) ?? t(fallbackKey);
}

/**
 * Кого дёрнуть на 401. Подпись initData живёт сутки (INITDATA_MAX_AGE_SECONDS),
 * и вкладка, провисевшая дольше, получает 401 на всё — дальше запросы
 * бессмысленны, нужен экран «открой приложение заново» (AppProvider).
 */
let unauthorizedHandler: (() => void) | null = null;
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const { initData } = telegramEnv();

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...(initData ? { authorization: `tma ${initData}` } : {}),
      ...init.headers,
    },
  });

  const text = await response.text();

  // не всякий ответ — наш JSON: 502/504 отдаёт nginx, и это HTML-страница.
  // Без этой защиты пользователь увидел бы сообщение парсера JSON
  let payload: unknown = null;
  try {
    payload = text.length > 0 ? (JSON.parse(text) as unknown) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    const serverMessage =
      payload && typeof payload === 'object' && 'error' in payload
        ? String((payload as { error: unknown }).error)
        : null;
    // техническая строка на случай, когда сервер ничего внятного не сказал:
    // экран заменит её на свой текст по флагу fromServer
    throw new ApiError(
      response.status,
      payload,
      serverMessage ?? `HTTP ${response.status}`,
      serverMessage !== null,
    );
  }

  if (payload === null && text.length > 0) {
    throw new ApiError(response.status, null, `HTTP ${response.status}`, false);
  }

  return payload as T;
}

// ---------- типы ответов ----------

export interface Me {
  user: {
    telegramUserId: string;
    email: string | null;
    mode: 'DEMO' | 'REAL';
    language: string;
    tokens: number;
    stage: string | null;
  };
  balances: { demo: number; real: number; currency: string };
  broker: {
    linked: boolean;
    traderId: string | null;
    isVerified: boolean;
    /** false — аккаунт заведён вне нашей партнёрки. */
    isPartnerClient: boolean | null;
  };
  settings: {
    strategy: string;
    progression: number;
    maxSteps: number;
    maxTrades: number;
    expirationSeconds: number;
    autotrade: boolean;
  };
}

export interface TradeStep {
  index: number;
  amount: number;
  action: 'call' | 'put';
  won: boolean;
  profit: number;
  runningProfit: number;
  /** Пара шага — только в стратегии-«корзине», где каждый шаг на своей паре. */
  pair?: string;
  /** Подпись этой пары: `USDMXN_otc` → `USD/MXN OTC`. */
  pairLabel?: string;
  /** Только сделки у брокера: id, цены входа/выхода, время и экспирация — для карточки сделки. */
  brokerTradeId?: string;
  openPrice?: number;
  closePrice?: number;
  openedAt?: number;
  closedAt?: number;
  expirationSeconds?: number;
}

/**
 * Состояние серии.
 *
 * Серия больше не считается «в одном ответе»: в режиме реальных демо-сделок шаг
 * длится настоящую экспирацию, поэтому сервер отдаёт `runId` сразу, а шаги
 * появляются по мере закрытия сделок у брокера.
 */
export interface TradeRun {
  runId: string;
  status: 'running' | 'done' | 'failed';
  pair: string;
  /** Подпись пары для экрана; `pair` остаётся символом брокера. */
  pairLabel?: string;
  action: 'call' | 'put';
  steps: TradeStep[];
  /** Сделка открыта и ждёт результата — её показываем отдельной строкой. */
  pending: {
    index: number;
    amount: number;
    action: 'call' | 'put';
    /** Пара именно этой сделки (автопилот и «корзина» меняют её от сделки к сделке). */
    pair?: string;
    pairLabel?: string;
    expirationSeconds?: number;
    openedAt?: number;
    /** Только сделки у брокера: цена входа и момент экспирации — для линии и таймера на графике. */
    openPrice?: number;
    closeTimestamp?: number;
  } | null;
  /** Что показывать в окне графика; `assetId: null` — графика по паре нет. */
  chart?: { pair: string; pairLabel: string; assetId: number | null; digits: number } | null;
  /** `autopilot` — сессия по времени, `custom` — сессия по правилам пользователя, `series` — обычная серия. */
  kind?: 'series' | 'autopilot' | 'custom';
  /** Автопилот и кастом: когда сессия закончится сама, ms epoch. */
  endsAt?: number | null;
  autopilot?: AutopilotParams | null;
  custom?: CustomParams | null;
  /** Кастом: журнал пропусков (слабый сигнал, новостное окно), последние записи. */
  events?: RunEvent[];
  /** Сессия: сколько серий закрыто и сколько запланировано (кастом: максимум серий). */
  cyclesDone?: number;
  cyclesPlanned?: number;
  profit: number;
  won: boolean;
  tokensSpent: number;
  tokensLeft: number;
  balance: number;
  /** true — сделки реально ушли брокеру, false — симуляция. */
  live: boolean;
  /** real — сделки идут на реальном счёте (настоящие деньги). */
  market?: 'demo' | 'real';
  errorKey: string | null;
}

export interface AutopilotParams {
  minPercent: number;
  maxPercent: number;
  durationMinutes: number;
}

/** 16 настроек стратегии «Кастом» — те же, что в боте; допустимые значения приходят в `options.custom`. */
export interface CustomParams {
  stake: number;
  progression: number;
  maxSteps: number;
  expirationSeconds: number;
  durationMinutes: number;
  maxTrades: number;
  stopLoss: number;
  takeProfit: number;
  maxLossStreak: number;
  pauseSeconds: number;
  direction: 'auto' | 'call' | 'put';
  minPayout: number;
  newsFilter: 'off' | 'medium' | 'strict';
  minConfidence: number;
  indicators: string[];
  pairs: string[];
}

export type CustomOptions = {
  [K in keyof Omit<CustomParams, 'pairs'>]: ReadonlyArray<CustomParams[K] extends (infer U)[] ? U : CustomParams[K]>;
};

export interface RunEvent {
  at: number;
  key: string;
  pair?: string;
  pairLabel?: string;
  value?: number;
}

export interface Asset {
  symbol: string;
  label: string | null;
  type: string | null;
  payout: number | null;
  minTimeframe: number | null;
  maxTimeframe: number | null;
}

export interface Stats {
  total: number;
  won: number;
  lost: number;
  winRate: number;
  profit: number;
  tokensSpent: number;
  /** Самая длинная серия побед подряд. */
  bestStreak: number;
  /** Пара, по которой торговали чаще всего. */
  favouritePair: string | null;
  /** По какому счёту цифры: сервер отдаёт текущий режим пользователя. */
  mode?: 'DEMO' | 'REAL';
  favouritePairLabel: string | null;
}

export interface TokenPackage {
  id: string;
  tokens: number;
  priceUsd: number;
  priceCryptoUsd: number;
  badge: 'star' | 'fire' | null;
}

export interface StrategyPreset {
  id: string;
  /** Витринное имя (PULSE, SNIPER…) — одинаково на всех языках. */
  label: string;
  /** Ключ i18n имени — у «Кастом» имя на языке пользователя (см. `strategyName`). */
  labelKey?: string;
  emoji: string;
  progression: number;
  maxSteps: number;
  maxTrades: number;
  minBalanceUsd: number | null;
  manual?: boolean;
  /** Механика серии; отсутствует = классический Smart Scaling (recovery). `auto` — сессия «Автопилот». */
  mode?: 'recovery' | 'flat' | 'paroli' | 'streak' | 'basket' | 'auto' | 'custom';
  /** streak: выигрышей до завершения серии. */
  targetWins?: number;
  /** basket: на скольких парах открываются сделки. */
  pairs?: number;
}

export interface SettingsPayload {
  current: {
    strategy: string;
    progression: number;
    maxSteps: number;
    maxTrades: number;
    expirationSeconds: number;
    autotrade: boolean;
    language: string;
    manual: { progression: number; maxSteps: number; maxTrades: number };
    autopilot?: AutopilotParams;
    custom?: CustomParams;
    favouritePairs: string[];
  };
  options: {
    strategies: StrategyPreset[];
    expirations: number[];
    manual: { progression: number[]; maxSteps: number[]; maxTrades: number[] };
    bets: Array<{ amount: number; risk?: boolean }>;
    autopilot?: { minPercent: number[]; maxPercent: number[]; durationMinutes: number[] };
    autopilotTradeCost?: number;
    custom?: CustomOptions;
    customPairsMax?: number;
    /** Токены за одну серию кастома при текущей глубине лесенки. */
    customSeriesCost?: number;
  };
}

export interface ReferralsPayload {
  link: string;
  stats: {
    /** Все приглашённые, в любом статусе — именно это число показывает экран. */
    total: number;
    invited: number;
    trading: number;
    confirmed: number;
    pending: number;
    tokensEarned: number;
  };
  referrals: Array<{
    referredId: string;
    status: 'invited' | 'trading' | 'confirmed';
    tokensAwarded: number;
    createdAt: string;
  }>;
}

// ---------- методы ----------

export interface AppConfig {
  brand: {
    name: string;
    /** Имя ИИ-ассистента на экране сигнала (BRAND_AI_NAME). */
    aiName: string;
    botUsername: string;
    botLink: string;
    supportContact: string;
    communityChannel: string;
  };
  /** Готовые ссылки на контакты (null — не задан): URL собирает сервер. */
  links?: {
    support: string | null;
    community: string | null;
    reviews: string | null;
  };
  locales: string[];
  defaultLocale: string;
  /** Тема оформления (мульти-бот); null/отсутствует — основная. */
  theme?: string | null;
  /** Пороги реф-начислений — с сервера, из того же конфига, что и реальные выплаты. */
  referralTiers?: Array<{ minDeposit: number; maxDeposit: number | null; tokens: number }>;
  /** Пул индикаторов «AI-скана»: каждый скан собирает из него случайный чек-лист. */
  scanIndicators?: ScanIndicatorGroup[];
  /** Токенов за $1 депозита — для текстов «{{rate}} за $1». */
  tokensPerDollar?: number;
  /** Настроен ли вход на сайте брокера (OAuth) — иначе кнопку не показываем. */
  brokerOAuth?: boolean;
}

export interface WalletSessionResponse {
  session: string;
  expiresIn: number;
  /** Адрес iframe — без пользователя, сессии и токена. */
  embedUrl: string;
  /** Только этому origin отдаём сессию и только от него принимаем события. */
  embedOrigin: string;
  /** Префикс postMessage-событий виджета. */
  eventPrefix: string;
  mode: 'deposit' | 'withdraw';
}

/** Старт терминала. `accessToken` живёт в памяти вкладки и нужен только сокету брокера. */
export interface TerminalBootstrap {
  /** `null` — сокета нет: терминал работает опросом. */
  wsUrl: string | null;
  userId: number;
  accessToken: string;
  tokenExpiresAt: number;
  /** Часы брокера в момент ответа. */
  brokerTime: number;
  assets: TerminalAsset[];
  minTradeAmount: number;
  balances: { real: number; demo: number };
  realAllowed: boolean;
  intervals: string[];
}

export interface ScanIndicatorGroup {
  label: string;
  metrics: Array<{ name: string; min: number; max: number; digits: number; suffix?: string }>;
}

export const api = {
  /** Имя бренда и контакты приходят с сервера — на фронте их не хардкодим. */
  config: () => request<AppConfig>(withBot('/api/config')),

  /** Картинки, заменённые через админку (BACKLOG D7); версия языка сильнее дефолта. */
  media: (locale?: string) =>
    request<{ media: Record<string, string> }>(`/api/media${locale ? `?locale=${locale}` : ''}`),

  /** Словарь интерфейса под язык пользователя — общий с ботом. */
  texts: (locale?: string) =>
    request<{ locale: string; texts: Record<string, string> }>(
      withBot(`/api/texts${locale ? `?locale=${locale}` : ''}`),
    ),

  me: () => request<Me>('/api/me'),

  /** Вход в брокера, шаг 1: код на почту. Паролей нет — ни у нас, ни у брокера. */
  requestLoginCode: (email: string) =>
    request<
      | { status: 'sent'; resendInSeconds: number }
      | { status: 'invalid_email' }
      | { status: 'rate_limited'; retryInSeconds: number }
    >('/api/account/code/request', { method: 'POST', body: JSON.stringify({ email }) }),

  /** Второй способ входа: ссылка на страницу авторизации брокера; `null` — не настроен. */
  oauthUrl: () => request<{ url: string | null }>('/api/account/oauth-url'),

  /** Шаг 2: код из письма. Нового пользователя брокер создаёт сам (`isNew`). */
  verifyLoginCode: (email: string, code: string) =>
    request<
      | { status: 'linked'; traderId: string; isNew: boolean; isPartnerClient: boolean | null }
      | { status: 'invalid_code' }
      | { status: 'blocked' }
      | { status: 'rate_limited'; retryInSeconds: number }
    >('/api/account/code/verify', { method: 'POST', body: JSON.stringify({ email, code }) }),

  /** Выход из аккаунта брокера: связь удаляется, режим возвращается в DEMO. */
  unlinkAccount: () =>
    request<{ unlinked: boolean }>('/api/account/unlink', { method: 'POST' }),

  /** DEMO ↔ REAL. REAL — только наш аккаунт с депозитом, иначе 409 с кодом. */
  setMode: (mode: 'DEMO' | 'REAL') =>
    request<{ mode: string }>('/api/account/mode', {
      method: 'POST',
      body: JSON.stringify({ mode }),
    }),

  runCycle: (input: { amount: number; pair?: string; action?: 'call' | 'put'; session?: boolean }) =>
    request<TradeRun>('/api/trade/cycle', { method: 'POST', body: JSON.stringify(input) }),

  /** Остановить серию или сессию автопилота: открытая сделка доживёт, новые не откроются. */
  stopRun: () => request<{ stopping: boolean }>('/api/trade/stop', { method: 'POST' }),

  /** Состояние серии — экран опрашивает его, пока `status === 'running'`. */
  tradeRun: (runId: string) => request<TradeRun>(`/api/trade/run/${runId}`),

  /** Незаконченная серия: если приложение закрыли на середине, вернёмся к ней. */
  activeRun: () => request<{ run: TradeRun | null }>('/api/trade/run'),

  assets: () => request<{ assets: Asset[] }>('/api/assets'),

  stats: () => request<Stats>('/api/stats'),

  packages: () => request<{ packages: TokenPackage[] }>('/api/tokens/packages'),

  /**
   * Одноразовая сессия iframe-виджета брокера (пополнение / вывод). Живёт минуту;
   * `renew` — повторный выпуск по просьбе виджета, клик в аналитике не дублируется.
   */
  walletSession: (input: { mode: 'deposit' | 'withdraw'; packageId?: string; stage?: string; renew?: boolean }) =>
    request<WalletSessionResponse>('/api/wallet/session', {
      method: 'POST',
      body: JSON.stringify({
        mode: input.mode,
        ...(input.packageId ? { packageId: input.packageId } : {}),
        ...(input.stage ? { stage: input.stage } : {}),
        ...(input.renew ? { renew: true } : {}),
      }),
    }),

  /** Сверка платежей у брокера сразу после операции в виджете: токены начисляет сервер. */
  walletSync: (stage?: string, credited = false) =>
    request<{ deposits: number; tokens: number; mode: string; balances: { real: number; realAvailable: number } }>(
      '/api/wallet/sync',
      { method: 'POST', body: JSON.stringify({ ...(stage ? { stage } : {}), ...(credited ? { credited: true } : {}) }) },
    ),

  /** Кабинет брокера через наш редирект (клик логируется, домен — зеркало). */
  platformLink: () => request<{ link: string }>('/api/platform-link', { method: 'POST' }),

  settings: () => request<SettingsPayload>(withBot('/api/settings')),

  updateSettings: (patch: {
    strategy?: string;
    expirationSeconds?: number;
    autotrade?: boolean;
    language?: string;
    favouritePairs?: string[];
    manual?: Partial<Record<'progression' | 'maxSteps' | 'maxTrades', number>>;
    autopilot?: Partial<AutopilotParams>;
    custom?: Partial<CustomParams>;
  }) => request<unknown>(withBot('/api/settings'), { method: 'PATCH', body: JSON.stringify(patch) }),

  referrals: () => request<ReferralsPayload>('/api/referrals'),

  // ---------- терминал ----------

  terminalBootstrap: () => request<TerminalBootstrap>('/api/terminal/bootstrap'),

  /** Свечи через наш кэш. `to` — правый край окна (подгрузка истории влево). */
  terminalChart: (input: { assetId: number; interval: string; bars?: number; to?: number }) => {
    const query = new URLSearchParams({ asset_id: String(input.assetId), interval: input.interval });
    if (input.bars !== undefined) query.set('bars', String(input.bars));
    if (input.to !== undefined) query.set('to', String(Math.floor(input.to)));
    return request<{ candles: Candle[]; brokerTime: number }>(`/api/terminal/chart?${query.toString()}`);
  },

  /** Сделка — только через наш сервер: гейты реального счёта и общий бюджет сделок. */
  terminalTrade: (input: { assetId: number; amount: number; action: 'up' | 'down'; duration: number; isDemo: boolean }) =>
    request<{ trade: ApiTrade }>('/api/terminal/trade', { method: 'POST', body: JSON.stringify(input) }),

  terminalTrades: (input: { status: 'open' | 'closed'; isDemo?: boolean; limit?: number; offset?: number }) => {
    const query = new URLSearchParams({ status: input.status });
    if (input.isDemo !== undefined) query.set('is_demo', String(input.isDemo));
    if (input.limit !== undefined) query.set('limit', String(input.limit));
    if (input.offset !== undefined) query.set('offset', String(input.offset));
    return request<{ trades: ApiTrade[] }>(`/api/terminal/trades?${query.toString()}`);
  },
};
